import React, { useContext } from 'react';

import {
	AdfDocumentRenderer,
	AttachmentsSpaceRenderer,
	AttachmentsSpaceRendererWithMediaNodeHydration,
	getMediaNodesFromAdfDocument,
	getPlainTextFromAdfDocument,
} from '@atlassian/notification-renderer';
import { type AdfDocument, type ContentBodyItem } from '@atlassian/notification-types/types';

import { NotificationsStoreContext } from '../../../notifications-context';
import { DocumentWrapper, QuoteAuthor, QuoteWrapper } from '../../../quote-wrapper';
import { TruncatedText } from '../../../truncated-text';

type ExpandableAdfDocumentProps = {
	document: AdfDocument;
	author?: ContentBodyItem['author'];
	isExpanded: boolean;
	isQuoted: boolean;
	cloudId?: string;
	attachmentsRenderStyle?:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration';
};

const ExpandableAdfDocument = ({
	isExpanded,
	document,
	author,
	isQuoted,
	cloudId,
	attachmentsRenderStyle = 'inlinePlainTextPlaceholder',
}: ExpandableAdfDocumentProps) => {
	const { userId } = useContext(NotificationsStoreContext);
	const Wrapper = isQuoted ? QuoteWrapper : DocumentWrapper;

	if (isExpanded) {
		return (
			<Wrapper>
				{isQuoted && <QuoteAuthor noFloat author={author} />}
				<AdfDocumentRenderer
					adf={document.data}
					cloudId={cloudId}
					userId={userId}
					attachmentsRenderStyle={attachmentsRenderStyle}
				/>
			</Wrapper>
		);
	}

	const mediaNodes = getMediaNodesFromAdfDocument(document.data);

	const truncateProps = isQuoted ? { clamp: 1 } : {};
	return (
		<Wrapper>
			{isQuoted && <QuoteAuthor tall author={author} />}
			<TruncatedText
				{...truncateProps}
				text={getPlainTextFromAdfDocument(document.data, attachmentsRenderStyle)}
			/>
			{attachmentsRenderStyle === 'attachmentsFooter' && mediaNodes.length > 0 && (
				<AttachmentsSpaceRenderer mediaNodes={mediaNodes} />
			)}
			{attachmentsRenderStyle === 'attachmentsFooterWithMediaHydration' &&
				mediaNodes.length > 0 && (
					<AttachmentsSpaceRendererWithMediaNodeHydration mediaNodes={mediaNodes} />
				)}
		</Wrapper>
	);
};

export default ExpandableAdfDocument;
